import { Component, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { CreateActivationChannelModalComponent } from '../create-activation-channel-modal/create-activation-channel-modal.component';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ActivationChannel,
  ConnectionStatus,
  DeleteActivationChannelMutation,
  DeleteActivationChannelMutationVariables,
  Engagements,
  GetActivationChannelsQuery,
  GetActivationChannelsQueryVariables,
} from '../../../../API';
import { GetActivationChannels } from '../../../../graphql/queries';
import { ExportAudienceModalComponent } from '../export-audience-modal/export-audience-modal.component';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { DeleteActivationChannel } from '../../../../graphql/mutations';

@Component({
  selector: 'app-activation-channel-list',
  templateUrl: './activation-channel-list.component.html',
  styleUrl: './activation-channel-list.component.scss',
})
export class ActivationChannelListComponent implements OnInit {
  engagement: Engagements | null = null;
  isLoading?: boolean;
  headers: any[] = [];
  activationChannels: ActivationChannel[] = [];
  lastModifiedByFilter: { text: string; value: string }[] = [];
  rowsToDisplay: ActivationChannel[] = [];
  totalCount?: number;
  readonly supportedPageSizes = [10, 25, 50];
  visible = false;
  searchValue?: string = '';

  constructor(
    private apiService: ApiService,
    public appService: AppService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement;
      this.getActivationChannels();
    });
  }

  getActivationChannels(): void {
    this.isLoading = true;
    this.apiService
      .query<GetActivationChannelsQuery, GetActivationChannelsQueryVariables>(GetActivationChannels, {
        engagementId: this.engagement!.engagement,
      })
      .then((response) => {
        this.setHeaders();
        this.activationChannels = this.rowsToDisplay = response.data?.GetActivationChannels as ActivationChannel[];
        this.setLastModifiedByFilter();
      })
      .catch((error) => {
        this.nzMessageService.error(
          `Failed to fetch activation channels because of ${error.errors[0].errorType} with message: ${error.errors[0].message}`,
        );
      })
      .finally(() => (this.isLoading = false));
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Channel Name',
        sortOrder: null,
        sortFn: (a: ActivationChannel, b: ActivationChannel) => a.name?.localeCompare(b.name ?? ''),
        sortDirections: ['ascend', 'descend', null],
        left: true,
      },
      {
        name: 'Channel Type',
        width: '4rem',
      },
      {
        name: 'Channel Description',
      },
      {
        name: 'Status',
        sortOrder: null,
        sortFn: (a: ActivationChannel, b: ActivationChannel) =>
          a.connectionStatus?.localeCompare(b.connectionStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: 'Success', value: 'SUCCESS' },
          { text: 'Fail', value: 'FAIL' },
          { text: 'Not Verified', value: 'NOT_VERIFIED' },
        ],
        filterFn: (type: string, queryRecord: ActivationChannel) => queryRecord.connectionStatus?.indexOf(type) !== -1,
        width: '3rem',
      },
      {
        name: 'Last Modified By',
        key: 'lastModifiedBy',
        listOfFilter: this.lastModifiedByFilter,
        filterFn: (lastModifiedByList: string[], queryRecord: ActivationChannel) =>
          lastModifiedByList.some(
            (lastModifiedBy) => queryRecord.lastModifiedBy && queryRecord.lastModifiedBy.indexOf(lastModifiedBy) !== -1,
          ),
        filterMultiple: true,
      },
      {
        name: 'Last Modified At',
        sortOrder: 'descend',
        sortFn: (a: ActivationChannel, b: ActivationChannel) =>
          new Date(a.lastModifiedAt ?? '').getTime() - new Date(b.lastModifiedAt ?? '').getTime(),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Action',
        width: '3rem',
        right: true,
      },
    ];
  }

  setLastModifiedByFilter(): any {
    this.headers.find((head) => head.key === 'lastModifiedBy').listOfFilter = this.activationChannels
      .map((record: any) => record.lastModifiedBy)
      .filter((lastModifiedBy, index, arr) => arr.indexOf(lastModifiedBy) === index)
      .map((lastModifiedBy) => {
        return {
          text: lastModifiedBy,
          value: lastModifiedBy,
        };
      });
  }

  getPageData(): void {
    this.totalCount = this.rowsToDisplay?.length;
  }

  reset(): void {
    this.searchValue = '';
    this.search();
  }

  search(): void {
    this.visible = false;
    this.rowsToDisplay = this.activationChannels?.filter(
      (item: any) => item.name?.toLowerCase().indexOf(this.searchValue?.toLowerCase()) !== -1,
    );
  }

  openCreateActivationChannelModal(): void {
    this.modalService.create({
      nzTitle: 'Create Channel',
      nzFooter: null,
      nzContent: CreateActivationChannelModalComponent,
      nzMaskClosable: false,
      nzWidth: '45rem',
      nzOnOk: () => {
        this.getActivationChannels();
      },
    });
  }

  openUpdateActivationChannelModal(activationChannel: ActivationChannel): void {
    this.modalService.create({
      nzTitle: 'Update Channel',
      nzFooter: null,
      nzContent: CreateActivationChannelModalComponent,
      nzData: activationChannel,
      nzWidth: '45rem',
      nzOnOk: () => {
        this.getActivationChannels();
      },
    });
  }

  openDeleteActivationChannelModal(activationChannel: ActivationChannel): void {
    this.modalService.confirm({
      nzTitle: 'Are you sure you want to delete this record?',
      nzContent: 'This action cannot be undone.',
      nzOkText: 'Yes, Delete',
      nzOnOk: () => this.handleDelete(activationChannel),
      nzCancelText: 'Cancel',
    });
  }

  handleDelete(activationChannel: ActivationChannel): void {
    this.isLoading = true;
    const { channelId, engagement } = activationChannel;

    this.apiService
      .query<DeleteActivationChannelMutation, DeleteActivationChannelMutationVariables>(DeleteActivationChannel, {
        activationChannelInput: {
          channelId,
          engagement,
        },
      })
      .then((data: any) => {
        this.nzMessageService.success(data.message || 'Record Deleted Successfully.');
        this.getActivationChannels();
      })
      .catch(() => {
        this.nzMessageService.error('Failed to delete record');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getConnectionStatusColor(status?: ConnectionStatus | null): string {
    switch (status) {
      case ConnectionStatus.SUCCESS:
        return 'success';
      case ConnectionStatus.NOT_VERIFIED:
        return 'warning';
      case ConnectionStatus.FAIL:
        return 'error';
      default:
        return '';
    }
  }

  exportAudience(activationChannel: ActivationChannel): void {
    this.modalService.create({
      nzTitle: 'Export Audience',
      nzFooter: null,
      nzContent: ExportAudienceModalComponent,
      nzWidth: '40rem',
      nzData: activationChannel,
      nzOnOk: () => {
        this.getActivationChannels();
      },
    });
  }

  canPerformAction(activationChannel: ActivationChannel): boolean {
    return activationChannel.orgType === this.userManagementService.getOrgType();
  }
}
